import React from 'react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getDashboardStats } from '../../services/apiServices';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    resumeSubmissions: 0,
    totalMatches: 0,
    scheduledCalls: 0,
    activePremiumSubscriptions: 0,
    newUsersThisMonth: 0,
    resumesSentThisMonth: 0,
    currentMonth: '',
    userData: [],
    userSubscriptionBreakdown: { total: 0, breakdown: [] }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const dashboardResponse = await getDashboardStats();

      if (dashboardResponse?.success && dashboardResponse?.data) {
        setDashboardData(dashboardResponse.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="right-section">
        {loading && (
          <div className="loader-overlay">
            <span className="loader"></span>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="right-section">
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            className="btn btn-outline-danger ms-3"
            onClick={fetchDashboardStats}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="right-section">
        <div className="row">
          <div className="col-sm-2">
            <div className="dashboard-box">
              <span>
                <img src={window.getPublicUrl('/images/active-user.svg')} alt="Total Users Icon" />

              </span>
              <h4>Total Users</h4>
              <h3>{formatNumber(dashboardData.totalUsers)}</h3>
            </div>
          </div>

          <div className="col-sm-2">
            <div className="dashboard-box">
              <span>
                <img src={window.getPublicUrl('/images/revenue.svg')} alt="Resumes Submitted Icon" />
              </span>
              <h4>Resumes Submitted</h4>
              <h3>{formatNumber(dashboardData.resumeSubmissions)}</h3>
            </div>
          </div>

          <div className="col-sm-2">
            <div className="dashboard-box">
              <span>
                <img src={window.getPublicUrl('/images/active-matches.svg')} alt="Active Matches Icon" />
              </span>
              <h4>Active Matches</h4>
              <h3>{formatNumber(dashboardData.totalMatches)}</h3>
            </div>
          </div>

          <div className="col-sm-2">
            <div className="dashboard-box">
              <span>
                <img src={window.getPublicUrl('/images/schedule-calls.svg')} alt="Scheduled Calls Icon" />
              </span>
              <h4>Scheduled Calls</h4>
              <h3>{formatNumber(dashboardData.scheduledCalls)}</h3>
            </div>
          </div>

          <div className="col-sm-2">
            <div className="dashboard-box">
              <span>
                <img src={window.getPublicUrl('/images/review.svg')} alt="New Users Icon" />
              </span>
              <h4>New Users This Month</h4>
              <h3>{formatNumber(dashboardData.newUsersThisMonth)}</h3>
            </div>
          </div>

          <div className="col-sm-2">
            <div className="dashboard-box">
              <span>
                <img src={window.getPublicUrl('/images/rewards.svg')} alt="Premium Subscribers Icon" />
              </span>
              <h4>Premium Subscribers (Active)</h4>
              <h3>{formatNumber(dashboardData.activePremiumSubscriptions)}</h3>
            </div>
          </div>

          <div className="col-sm-8">
            <div className="sales-graph">
              <div className="d-flex justify-content-between">
                <h3>User Registration & Resume Submissions (12 Months)</h3>
                <div className="tab">
                  <ul>
                    <li className="active">12 Months</li>
                    <li>6 Months</li>
                    <li>30 Days</li>
                    <li>7 Days</li>
                  </ul>
                </div>
              </div>
              <div className="graph" style={{ height: '300px', padding: '20px' }}>
                <Line
                  data={{
                    labels: dashboardData.userData.map(item => item.month),
                    datasets: [
                      {
                        label: 'User Registration',
                        data: dashboardData.userData.map(item => item.userRegister),
                        borderColor: '#ff6b8a',
                        backgroundColor: 'rgba(255, 107, 138, 0.2)',
                        tension: 0.4,
                        fill: true,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: '#ff6b8a',
                        pointHoverBorderColor: '#fff',
                        pointHoverBorderWidth: 2,
                      },
                      {
                        label: 'Resume Submissions',
                        data: dashboardData.userData.map(item => item.sendResume),
                        borderColor: '#ff6b8a',
                        backgroundColor: 'rgba(255, 107, 138, 0.1)',
                        tension: 0.4,
                        fill: '+1',
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: '#ff6b8a',
                        pointHoverBorderColor: '#fff',
                        pointHoverBorderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: '#fff',
                        titleColor: '#333',
                        bodyColor: '#333',
                        borderColor: '#ddd',
                        borderWidth: 1,
                        cornerRadius: 8,
                        displayColors: false,
                        titleFont: {
                          size: 14,
                          weight: 'bold',
                        },
                        bodyFont: {
                          size: 16,
                          weight: 'bold',
                        },
                        callbacks: {
                          title: function(context) {
                            return context[0].label + ' 2025';
                          },
                          label: function(context) {
                            return context.parsed.y;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        display: true,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          color: '#666',
                          font: {
                            size: 12,
                          },
                        },
                      },
                      y: {
                        display: true,
                        grid: {
                          color: '#f0f0f0',
                          drawBorder: false,
                        },
                        ticks: {
                          display: false,
                        },
                        beginAtZero: true,
                      },
                    },
                    interaction: {
                      intersect: false,
                      mode: 'index',
                    },
                    elements: {
                      line: {
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-sm-4">
            <div className="sales-graph-active">
              <h3 className="text-center mb-2">Active Subscription Overview</h3>
              <div className="percentage-graph text-center" style={{ height: '200px', marginBottom: '20px' }}>
                <Doughnut
                  data={{
                    labels: dashboardData.userSubscriptionBreakdown.breakdown.map(item => item.type),
                    datasets: [{
                      data: dashboardData.userSubscriptionBreakdown.breakdown.map(item => item.count),
                      backgroundColor: dashboardData.userSubscriptionBreakdown.breakdown.map(item => item.color),
                      borderWidth: 2,
                      borderColor: '#fff',
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 20,
                          usePointStyle: true,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const label = context.label || '';
                            const value = context.parsed;
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = Math.round((value / total) * 100);
                            return `${label}: ${value} (${percentage}%)`;
                          }
                        }
                      },
                    },
                    cutout: '60%',
                  }}
                />
              </div>

              {dashboardData.userSubscriptionBreakdown.breakdown.map((item, index) => (
                <div key={index} className={`user-stat ${index < dashboardData.userSubscriptionBreakdown.breakdown.length - 1 ? 'mb-3' : ''}`}>
                  <div className="premium-data d-flex justify-content-between">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          borderRadius: '50%',
                          marginRight: '8px'
                        }}
                      ></div>
                      <p style={{ margin: 0 }}>
                        {item.type}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>{formatNumber(item.count)}</p>
                      <small style={{ color: '#666' }}>{item.percentage}%</small>
                    </div>
                  </div>
                </div>
              ))}

            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;